import React, { useEffect, useState, useCallback } from "react";
import BrimiLogo from "@/components/BrimiLogo";

/**
 * Celebration burst — fires a short sun/ray sparkle animation from the center
 * of the screen. Use imperatively via the `celebrate()` global function.
 *
 * Example:
 *   import { celebrate } from "@/components/Celebrate";
 *   celebrate("Roadmap ready!");
 */

let _emit = null;

export function celebrate(message) {
  if (_emit) _emit(message);
}

const COLORS = ["#FBBF24", "#FB7185", "#34D399", "#3B82F6", "#F97316", "#8B5CF6"];

export default function Celebrate() {
  const [state, setState] = useState(null); // { id, message }

  const trigger = useCallback((message) => {
    setState({ id: Date.now(), message: message || null });
  }, []);

  useEffect(() => {
    _emit = trigger;
    return () => { _emit = null; };
  }, [trigger]);

  useEffect(() => {
    if (!state) return;
    const t = setTimeout(() => setState(null), 1600);
    return () => clearTimeout(t);
  }, [state]);

  if (!state) return null;

  // Generate 24 particles in a radial burst
  const particles = Array.from({ length: 24 }).map((_, i) => {
    const angle = (i / 24) * Math.PI * 2;
    const distance = 180 + Math.random() * 120;
    const dx = Math.cos(angle) * distance;
    const dy = Math.sin(angle) * distance;
    const color = COLORS[i % COLORS.length];
    const size = 8 + Math.random() * 8;
    const delay = Math.random() * 120;
    return { i, dx, dy, color, size, delay };
  });

  return (
    <div className="brimi-burst" key={state.id} data-testid="celebrate-burst">
      {particles.map((p) => (
        <span
          key={p.i}
          className="brimi-burst-particle"
          style={{
            "--dx": `${p.dx}px`,
            "--dy": `${p.dy}px`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: p.color,
            animationDelay: `${p.delay}ms`,
            boxShadow: `0 0 16px ${p.color}`,
          }}
        />
      ))}
      {/* Center logo pulse */}
      <div
        style={{
          position: "absolute", top: "50%", left: "50%",
          transform: "translate(-50%, -50%)",
          animation: "brimi-sun-rise 800ms cubic-bezier(0.22, 1, 0.36, 1) both",
        }}
      >
        <BrimiLogo size={72} withBadge variant="cream" animated={false} />
      </div>
      {state.message && (
        <div
          style={{
            position: "absolute",
            top: "calc(50% + 60px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(11,17,32,0.85)",
            color: "white",
            padding: "8px 16px",
            borderRadius: "9999px",
            fontWeight: 700,
            fontSize: "14px",
            whiteSpace: "nowrap",
            backdropFilter: "blur(8px)",
          }}
        >
          {state.message}
        </div>
      )}
    </div>
  );
}
